import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { colors, spacing, radius, typography, shadows, transitions } from '../../styles/monochromeTheme';
import {
  Table,
  Card,
  Button,
  message,
  Popconfirm,
  Tag,
  Space,
  Input,
  InputNumber,
  Modal,
  Form,
  Row,
  Col,
  Divider,
  Dropdown,
  Typography
} from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  EditOutlined,
  ReloadOutlined,
  GoogleOutlined,
  SearchOutlined,
  LinkOutlined,
  FileTextOutlined,
  SendOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  DollarOutlined,
  CreditCardOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { supabase } from '../../api/supabase';
import { generateDocumentWithGoogleDrive, checkGoogleDriveStatus, backupAllTemplates } from '../../utils/googleDocumentGenerator';
import { getGoogleAuthUrl, exportDocToPdf, isGoogleAuthenticated } from '../../api/googleDrive';
import { sendForSignature, getSignatureStatus } from '../../api/signature';
import dayjs from 'dayjs';
import CharterEditDrawer from './CharterEditDrawer';

const { Title, Text } = Typography;

// Monochrome styles
// ─── Pill group tokens (monochrome) ─────────────────────────────────────────
const pill = {
  container: {
    display: 'inline-flex',
    alignItems: 'stretch',
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #e5e5e5',
    background: '#ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  divider: {
    width: 1,
    alignSelf: 'stretch',
    background: '#e5e5e5',
    flexShrink: 0,
  },
  segment: (filled) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '5px 12px',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    background: filled ? '#111' : '#ffffff',
    color: filled ? '#fff' : '#555',
    border: 'none',
    outline: 'none',
    whiteSpace: 'nowrap',
    letterSpacing: '0.01em',
    transition: 'background 0.12s, color 0.12s',
  }),
  segmentGhost: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '5px 12px',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    background: '#f7f7f7',
    color: '#999',
    border: 'none',
    outline: 'none',
    whiteSpace: 'nowrap',
    letterSpacing: '0.01em',
  },
};

const docAccent = { 'Contrat': true, 'Confirmation': true, 'Facturation': true };

const styles = {
  page: {
    padding: spacing.xxxxl,
    maxWidth: '100%',
    minHeight: '100vh',
    background: '#f7f7f7',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xxl,
    background: '#ffffff',
    borderRadius: radius.xxxl,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    border: '1px solid #ebebeb',
    padding: `${spacing.lg} ${spacing.xxl}`,
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerIcon: {
    width: '44px',
    height: '44px',
    borderRadius: radius.xl,
    background: '#111',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
  },
  headerTitle: {
    margin: 0,
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: '#111',
    letterSpacing: typography.letterSpacing.tight,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: spacing.md,
    marginBottom: spacing.xxl,
  },
  statCard: (active) => ({
    background: active ? '#111' : '#ffffff',
    borderRadius: radius.xl,
    border: `1px solid ${active ? '#111' : '#ebebeb'}`,
    padding: spacing.lg,
    cursor: 'pointer',
    transition: transitions.normal,
    boxShadow: active ? '0 4px 16px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
  }),
  statNumber: {
    fontSize: typography.fontSize.xxl,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
    display: 'block',
  },
  statLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: typography.letterSpacing.wide,
    display: 'block',
  },
  tableCard: {
    background: '#ffffff',
    borderRadius: radius.xxxl,
    border: '1px solid #ebebeb',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
  },
  tableCardHeader: {
    padding: `${spacing.lg} ${spacing.xxl}`,
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
  },
  primaryBtn: {
    height: '44px',
    borderRadius: radius.lg,
    background: '#111',
    border: 'none',
    color: '#fff',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
  },
  secondaryBtn: {
    height: '40px',
    borderRadius: radius.lg,
    background: '#fff',
    border: '1px solid #e0e0e0',
    color: '#555',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  tag: {
    borderRadius: radius.sm,
    padding: `${spacing.xs} ${spacing.md}`,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
  },
  clientAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: '#111',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: typography.fontWeight.bold,
    fontSize: typography.fontSize.sm,
    flexShrink: 0,
  },
  docBtn: () => pill.segment(true),
  docBtnOutline: () => pill.segmentGhost,
  editBtn: {
    ...pill.segment(false),
    border: 'none',
    height: 'auto',
  },
};

function DocumentListPage() {
  const [documents, setDocuments] = useState([]);
  const [legacyDocuments, setLegacyDocuments] = useState([]);
  const [allDocuments, setAllDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [legacyLoading, setLegacyLoading] = useState(true);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
  const [facturationModalVisible, setFacturationModalVisible] = useState(false);
  const [selectedCharter, setSelectedCharter] = useState(null);
  const [generatingInvoiceNumber, setGeneratingInvoiceNumber] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all', 'bateaux', 'seabob', 'jetski', 'lunch'
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'v2', 'legacy'
  const [form] = Form.useForm();
  const [invoiceForm] = Form.useForm();

  // Yousign state
  const [signatureModalVisible, setSignatureModalVisible] = useState(false);
  const [signatureForm] = Form.useForm();
  const [signatureSending, setSignatureSending] = useState(false);
  const [signatureTarget, setSignatureTarget] = useState(null); // { doc, charter }
  const [signatureMode, setSignatureMode] = useState('signature'); // 'signature' | 'signature_payment'

  // Charter edit drawer
  const [charterEditVisible, setCharterEditVisible] = useState(false);
  const [charterEditTarget, setCharterEditTarget] = useState(null);

  // Today's invoices counter
  const [todayInvoices, setTodayInvoices] = useState([]);
  const [invoiceCounterModalVisible, setInvoiceCounterModalVisible] = useState(false);

  // Google Drive mode state - always use Google Drive API
  const [googleStatus, setGoogleStatus] = useState({ configured: false, authenticated: false });
  const [backingUpTemplates, setBackingUpTemplates] = useState(false);

  // Check Google Drive status on mount
  useEffect(() => {
    const status = checkGoogleDriveStatus();
    setGoogleStatus(status);
  }, []);

  // Legacy Google Sheets configuration
  const LEGACY_SHEETS = [
    { id: '1Zvzuic_2G8LRguYZDx016BzMfC1sOaetEbDHh1G5fy4', sheet: '2024', category: 'bateaux', type: 'default' },
    { id: '1z4gf5M3uarriPt9FY7VsknQyjIAwJyHFjjnxEcL-oo8', sheet: '2024', category: 'lunch', type: 'default' },
    { id: '1KtS55J_b_RbaDi_-nRt_9tHbCL2N3aEOd0PbFUV4Z7g', sheet: '2O24', category: 'seabob', type: 'seabobs' },
    { id: '1eq0j5VvAxXb_EGSsM36Xqlj_NokW3DHTUC5jABWMisU', sheet: '2024', category: 'jetski', type: 'default' },
  ];

  // Fetch all legacy data from Google Sheets
  const fetchAllLegacyData = async () => {
    setLegacyLoading(true);
    const apiKey = 'AIzaSyCyG_AVr4QlTZ_HYipobuRRnYQBe7YmROA';
    const allLegacy = [];

    for (const config of LEGACY_SHEETS) {
      try {
        const range = `${config.sheet}!B1:Z`;
        const response = await axios.get(
          `https://sheets.googleapis.com/v4/spreadsheets/${config.id}?ranges=${range}&fields=sheets(data(rowData(values(effectiveValue,hyperlink))))&key=${apiKey}`
        );

        const rows = response.data.sheets[0]?.data[0]?.rowData;
        if (rows && rows.length > 1) {
          const headers = rows[0].values || [];
          const headerMap = {};
          headers.forEach((header, idx) => {
            const headerName = header?.effectiveValue?.stringValue || '';
            if (headerName) headerMap[headerName.trim()] = idx;
          });

          const formattedData = rows.slice(1).map((row, index) => {
            const values = row.values || [];
            const isSeabob = config.type === 'seabobs';

            return {
              key: `legacy-${config.category}-${index}`,
              charter_id: `legacy-${config.category}-${index}`,
              source: 'legacy',
              category: config.category,
              spreadsheetId: config.id,
              sheetName: config.sheet,
              rowIndex: rows.length - index - 1,
              client_name: values[headerMap['Nom du client'] || (isSeabob ? 2 : 1)]?.effectiveValue?.stringValue || '',
              charter_date: isSeabob
                ? (values[headerMap['Date du Charter'] || 1]?.effectiveValue?.stringValue || '')
                : (values[headerMap['Date du Charter'] || 0]?.effectiveValue?.stringValue || ''),
              created_at: isSeabob
                ? (values[headerMap['Date de la Réservation'] || 0]?.effectiveValue?.stringValue || '')
                : null,
              documents: {
                Contrat: values[headerMap['Contrat'] || (isSeabob ? 3 : 2)]?.hyperlink || null,
                modifContrat: values[headerMap['Modif Contrat'] || (isSeabob ? 4 : 3)]?.hyperlink || null,
                Confirmation: values[headerMap['Confirmation'] || (isSeabob ? 5 : 4)]?.hyperlink || null,
                modifConfirmation: values[headerMap['Modif Confirmation'] || (isSeabob ? 6 : 5)]?.hyperlink || null,
                Facturation: values[headerMap['Facturation'] || (isSeabob ? 7 : 6)]?.hyperlink || null,
                modifFacturation: values[headerMap['Modif Facturation'] || (isSeabob ? 8 : 7)]?.hyperlink || null,
              }
            };
          }).filter(item => item.client_name).reverse();

          allLegacy.push(...formattedData);
        }
      } catch (err) {
        console.error(`Error fetching legacy data for ${config.category}:`, err);
      }
    }

    setLegacyDocuments(allLegacy);
    setLegacyLoading(false);
  };

  // Combine V2 and Legacy documents
  useEffect(() => {
    const combined = [
      ...documents.map(d => ({ ...d, source: 'v2' })),
      ...legacyDocuments
    ];
    setAllDocuments(combined);
    applyFilters(combined, categoryFilter, sourceFilter);
  }, [documents, legacyDocuments]);

  // Helper function to generate document using selected mode
  // Always use Google Drive API for document generation
  const generateDocWithMode = async (type, data, charterId, category) => {
    try {
      const result = await generateDocumentWithGoogleDrive(type, data, charterId, category);
      message.success(
        <span>
          Document created! <a href={result.googleDocUrl} target="_blank" rel="noopener noreferrer">Open in Google Docs</a>
        </span>,
        10
      );
      return result;
    } catch (error) {
      if (error.message.startsWith('NOT_AUTHENTICATED:')) {
        const authUrl = error.message.replace('NOT_AUTHENTICATED:', '');
        message.warning('Please authenticate with Google first');
        window.location.href = authUrl;
        return null;
      }
      throw error;
    }
  };

  const handleConnectGoogle = () => {
    const authUrl = getGoogleAuthUrl();
    window.location.href = authUrl;
  };

  useEffect(() => {
    fetchDocuments();
    fetchAllLegacyData();
    fetchTodayInvoices();
  }, []);

  // Fetch today's invoices for the counter
  const fetchTodayInvoices = async () => {
    try {
      const today = dayjs();
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('type', 'Facturation')
        .gte('created_at', today.startOf('day').toISOString())
        .lte('created_at', today.endOf('day').toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;
      setTodayInvoices(data || []);
    } catch (error) {
      console.error('Error fetching today invoices:', error);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Grouper les documents par charter_id
      const grouped = {};
      data.forEach(doc => {
        const key = doc.charter_id || doc.id; // Fallback pour les anciens documents
        if (!grouped[key]) {
          grouped[key] = {
            charter_id: key,
            client_name: doc.client_name,
            charter_date: doc.charter_date,
            created_at: doc.created_at,
            category: doc.category || 'bateaux', // Par défaut 'bateaux' pour les anciens documents
            documents: {}
          };
        }
        grouped[key].documents[doc.type] = doc;
      });

      const groupedArray = Object.values(grouped);
      setDocuments(groupedArray);
      applyFilters(groupedArray, categoryFilter);
    } catch (error) {
      message.error('Erreur lors du chargement: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCharter = async (charterId, documents) => {
    try {
      // Supprimer tous les documents de ce charter
      const documentIds = Object.values(documents).map(doc => doc.id);

      const { error } = await supabase
        .from('documents')
        .delete()
        .in('id', documentIds);

      if (error) throw error;

      message.success('Charter et tous ses documents supprimés avec succès');
      fetchDocuments(); // Rafraîchir la liste
    } catch (error) {
      message.error('Erreur lors de la suppression: ' + error.message);
    }
  };

  const applyFilters = (docs, category, source = 'all', searchValue = '') => {
    let filtered = docs;

    // Filtrer par source (V2 ou Legacy)
    if (source !== 'all') {
      filtered = filtered.filter(charter => charter.source === source);
    }

    // Filtrer par catégorie
    if (category !== 'all') {
      filtered = filtered.filter(charter => charter.category === category);
    }

    // Filtrer par recherche
    if (searchValue) {
      filtered = filtered.filter(charter =>
        charter.client_name?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    setFilteredDocuments(filtered);
  };

  const handleSearch = (value) => {
    applyFilters(allDocuments, categoryFilter, sourceFilter, value);
  };

  const handleCategoryFilter = (category) => {
    setCategoryFilter(category);
    applyFilters(allDocuments, category, sourceFilter);
  };

  const handleSourceFilter = (source) => {
    setSourceFilter(source);
    applyFilters(allDocuments, categoryFilter, source);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      // Check if document was created via Google Drive
      if (doc.google_doc_id) {
        message.loading('Export PDF en cours...', 0);

        try {
          // Export directly to PDF from Google Drive
          const pdfBlob = await exportDocToPdf(doc.google_doc_id);

          // Create download link
          const url = window.URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${doc.type}_${doc.client_name || 'document'}.pdf`;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          message.destroy();
          message.success('PDF telecharge avec succes!');
        } catch (exportError) {
          message.destroy();
          // If not authenticated, redirect to Google auth
          if (exportError.message.includes('Not authenticated')) {
            message.warning('Veuillez vous connecter a Google Drive');
            const authUrl = getGoogleAuthUrl();
            window.location.href = authUrl;
          } else {
            throw exportError;
          }
        }
        return;
      }

      // Local file download logic
      const downloadUrl = doc.pdf_url || doc.docx_url;
      const isPdf = !!doc.pdf_url;

      console.log('Telechargement:', {
        isPdf,
        pdf_url: doc.pdf_url,
        docx_url: doc.docx_url,
        downloadUrl
      });

      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error('Erreur lors du telechargement');
      }

      const blob = await response.blob();

      // Creer un lien temporaire pour telecharger
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extraire le nom du fichier depuis l'URL et forcer l'extension correcte
      const urlParts = downloadUrl.split('/');
      let fileName = urlParts[urlParts.length - 1];

      // Si on telecharge un PDF mais que le nom contient .docx, le corriger
      if (isPdf && fileName.includes('.docx')) {
        fileName = fileName.replace('.docx', '.pdf');
      }

      link.download = fileName || `${doc.type}_${doc.client_name}.${isPdf ? 'pdf' : 'docx'}`;

      console.log('Fichier telecharge:', { fileName: link.download, isPdf });

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success(`Document ${isPdf ? 'PDF' : 'DOCX'} telecharge avec succes!`);
    } catch (error) {
      message.error('Erreur lors du telechargement: ' + error.message);
    }
  };

  const handlePreview = (record) => {
    setSelectedDocument(record);
    setPreviewVisible(true);
  };

  const handleEdit = (record) => {
    // If it's a Google Doc, open in Google Docs for editing
    if (record.google_doc_id) {
      const googleDocUrl = `https://docs.google.com/document/d/${record.google_doc_id}/edit`;
      window.open(googleDocUrl, '_blank');
      message.info('Document ouvert dans Google Docs pour modification');
      return;
    }

    // Otherwise use local edit modal
    setSelectedDocument(record);
    form.setFieldsValue(record.document_data);
    setEditVisible(true);
  };

  const handleRegenerateDocument = async (values) => {
    setRegenerating(true);
    try {
      // Régénérer le document avec les nouvelles données via Google Drive
      const category = selectedDocument.category || 'bateaux';
      const result = await generateDocWithMode(selectedDocument.type, values, selectedDocument.charter_id, category);

      if (result) {
        // Mettre à jour l'enregistrement dans la base de données
        const { error: updateError } = await supabase
          .from('documents')
          .update({
            document_data: values,
            google_doc_id: result.googleDocId,
          })
          .eq('id', selectedDocument.id);

        if (updateError) throw updateError;

        message.success('Document régénéré avec succès!');
        setEditVisible(false);
        fetchDocuments(); // Rafraîchir la liste
      }
    } catch (error) {
      message.error('Erreur lors de la régénération: ' + error.message);
    } finally {
      setRegenerating(false);
    }
  };

  const handleGenerateConfirmation = (charter) => {
    setSelectedCharter(charter);
    setConfirmationModalVisible(true);
  };

  const handleGenerateFacturation = async (charter) => {
    setSelectedCharter(charter);
    setGeneratingInvoiceNumber(true);

    try {
      // Auto-générer le numéro de facture
      const today = dayjs();
      const dateStr = today.format('DDMMYYYY');

      // Compter combien de factures ont déjà été générées aujourd'hui
      const { data: todayInvoicesData, error } = await supabase
        .from('documents')
        .select('*')
        .eq('type', 'Facturation')
        .gte('created_at', today.startOf('day').toISOString())
        .lte('created_at', today.endOf('day').toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Mettre à jour le state du compteur pour affichage immédiat
      setTodayInvoices(todayInvoicesData || []);

      const invoiceCount = (todayInvoicesData || []).length + 1;
      const invoiceNumber = `${dateStr}-${invoiceCount}`;

      invoiceForm.setFieldsValue({ invoice_number: invoiceNumber });
      setFacturationModalVisible(true);
    } catch (error) {
      message.error('Erreur lors de la génération du numéro de facture: ' + error.message);
    } finally {
      setGeneratingInvoiceNumber(false);
    }
  };

  const confirmGenerateConfirmation = async () => {
    setRegenerating(true);
    try {
      const contrat = selectedCharter.documents['Contrat'];
      if (!contrat) {
        throw new Error('Contrat introuvable');
      }

      const category = selectedCharter.category || 'bateaux';
      const oldData = contrat.document_data;

      let documentData;

      if (category === 'seabob' || category === 'jetski' || category === 'lunch') {
        // Pour les contrats Seabob, Jet-ski et Lunch, utiliser les données telles quelles
        documentData = {
          ...oldData,
          today_date: dayjs().format('DD MMMM YYYY'),
          charter_date: oldData.charterer_date || oldData.charter_date || oldData.charter_date,
        };
      } else {
        // Pour les contrats bateaux, reformater les prix
        const extractNumericValue = (value) => {
          if (!value) return null;
          const match = String(value).match(/[\d.]+/);
          return match ? parseFloat(match[0]) : null;
        };

        const boat_price_value = extractNumericValue(oldData.boat_price);
        const transport_price_value = extractNumericValue(oldData.transport_price);
        const taxes_price_value = extractNumericValue(oldData.taxes_price);
        const discount_price_value = extractNumericValue(oldData.discount_price);
        const extra_hour_price_value = extractNumericValue(oldData.extra_hour_price);
        const custom_price_value = extractNumericValue(oldData.custom_price);
        const total_price_value = extractNumericValue(oldData.total_price);

        documentData = {
          ...oldData,
          today_date: dayjs().format('DD MMMM YYYY'),
          generation_date: dayjs().format('DD MMMM YYYY'),
          boat_price: boat_price_value ? `${boat_price_value}€ (BOAT PRICE)` : '',
          transport_price: transport_price_value ? `+ ${transport_price_value}€` : '',
          taxes_price: taxes_price_value ? `+ ${taxes_price_value}€` : '',
          discount_price: discount_price_value ? `- ${discount_price_value}€` : '',
          extra_hour_price: extra_hour_price_value ? `+ ${extra_hour_price_value}€` : '',
          custom_price: custom_price_value ? `+ ${custom_price_value}€` : '',
          total_price: total_price_value ? `${total_price_value}€` : '',
        };
      }

      // Générer la Confirmation avec le même charter_id et category
      const result = await generateDocWithMode('Confirmation', documentData, contrat.charter_id, category);

      if (result) {
        setConfirmationModalVisible(false);
        fetchDocuments(); // Rafraîchir la liste
      }
    } catch (error) {
      message.error('Erreur lors de la génération de la Confirmation: ' + error.message);
    } finally {
      setRegenerating(false);
    }
  };

  const confirmGenerateFacturation = async (values) => {
    setRegenerating(true);
    try {
      const contrat = selectedCharter.documents['Contrat'];
      if (!contrat) {
        throw new Error('Contrat introuvable');
      }

      const category = selectedCharter.category || 'bateaux';
      const oldData = contrat.document_data;

      let documentData;

      if (category === 'seabob' || category === 'jetski' || category === 'lunch') {
        // Pour les contrats Seabob, Jet-ski et Lunch, utiliser les données telles quelles
        documentData = {
          ...oldData,
          today_date: dayjs().format('DD/MM/YYYY'),
          invoice_number: values.invoice_number,
          charter_date: oldData.charterer_date || oldData.charter_date,
          charterer: values.charterer || oldData.charterer || oldData.client_name, // Utiliser le charterer existant ou le nom du client
        };
      } else {
        // Pour les contrats bateaux, reformater les prix
        const extractNumericValue = (value) => {
          if (!value) return null;
          const match = String(value).match(/[\d.]+/);
          return match ? parseFloat(match[0]) : null;
        };

        const boat_price_value = extractNumericValue(oldData.boat_price);
        const transport_price_value = extractNumericValue(oldData.transport_price);
        const taxes_price_value = extractNumericValue(oldData.taxes_price);
        const discount_price_value = extractNumericValue(oldData.discount_price);
        const extra_hour_price_value = extractNumericValue(oldData.extra_hour_price);
        const custom_price_value = extractNumericValue(oldData.custom_price);
        const total_price_value = extractNumericValue(oldData.total_price);

        documentData = {
          ...oldData,
          today_date: dayjs().format('DD MMMM YYYY'),
          generation_date: dayjs().format('DD MMMM YYYY'),
          invoice_number: values.invoice_number,
          boat_price: boat_price_value ? `${boat_price_value}€ (BOAT PRICE)` : '',
          transport_price: transport_price_value ? `+ ${transport_price_value}€` : '',
          taxes_price: taxes_price_value ? `+ ${taxes_price_value}€` : '',
          discount_price: discount_price_value ? `- ${discount_price_value}€` : '',
          extra_hour_price: extra_hour_price_value ? `+ ${extra_hour_price_value}€` : '',
          custom_price: custom_price_value ? `+ ${custom_price_value}€` : '',
          total_price: total_price_value ? `${total_price_value}€` : '',
        };
      }

      // Générer la Facturation avec le même charter_id et category
      const result = await generateDocWithMode('Facturation', documentData, contrat.charter_id, category);

      if (result) {
        setFacturationModalVisible(false);
        invoiceForm.resetFields();

        // Rafraîchir immédiatement le compteur de factures (avec await pour s'assurer que c'est fait)
        await fetchTodayInvoices();

        // Puis rafraîchir la liste des documents
        fetchDocuments();
      }
    } catch (error) {
      message.error('Erreur lors de la génération de la Facturation: ' + error.message);
    } finally {
      setRegenerating(false);
    }
  };

  // Yousign handlers
  const handleSignatureSend = (doc, charter, mode = 'signature') => {
    setSignatureTarget({ doc, charter });
    setSignatureMode(mode);
    signatureForm.setFieldsValue({
      clientName: charter.client_name || '',
      clientEmail: '',
      documentTitle: `${doc.type} - ${charter.client_name || ''}`,
      paymentAmount: charter.total_amount || undefined,
    });
    setSignatureModalVisible(true);
  };

  const confirmSignatureSend = async (values) => {
    setSignatureSending(true);
    try {
      const doc = signatureTarget.doc;

      // Get PDF URL - export from Google Drive if needed
      let pdfUrl = doc.pdf_url;
      if (!pdfUrl && doc.google_doc_id) {
        message.loading('Export PDF depuis Google Drive...', 0);
        const pdfBlob = await exportDocToPdf(doc.google_doc_id);
        pdfUrl = `https://docs.google.com/document/d/${doc.google_doc_id}/export?format=pdf`;
        message.destroy();
      }

      if (!pdfUrl) {
        throw new Error('Aucun PDF disponible pour ce document');
      }

      const options = {};
      if (signatureMode === 'signature_payment' && values.paymentAmount) {
        options.paymentAmount = values.paymentAmount;
        options.documentId = doc.id;
      }

      const result = await sendForSignature(
        pdfUrl,
        values.clientEmail,
        values.clientName,
        values.documentTitle,
        options
      );

      // Save signature request ID to Supabase
      const updateData = {
        signature_request_id: result.signatureRequestId,
        signature_status: 'ongoing',
      };
      if (signatureMode === 'signature_payment' && values.paymentAmount) {
        updateData.payment_amount = values.paymentAmount;
        updateData.payment_email = values.clientEmail;
        updateData.payment_status = 'waiting_signature';
      }
      await supabase
        .from('documents')
        .update(updateData)
        .eq('id', doc.id);

      const modeLabel = signatureMode === 'signature_payment' ? 'signature + paiement' : 'signature';
      message.success(`Document envoyé à ${values.clientEmail} pour ${modeLabel}`);
      setSignatureModalVisible(false);
      signatureForm.resetFields();
      fetchDocuments();
    } catch (error) {
      console.error('Yousign error:', error);
      message.error(`Erreur signature: ${error.message}`);
    } finally {
      setSignatureSending(false);
    }
  };

  const handleRefreshSignatureStatus = async (doc) => {
    if (!doc.signature_request_id) return;

    try {
      const status = await getSignatureStatus(doc.signature_request_id);

      await supabase
        .from('documents')
        .update({ signature_status: status.status })
        .eq('id', doc.id);

      message.info(`Statut signature: ${status.status}`);
      fetchDocuments();
    } catch (error) {
      message.error(`Erreur: ${error.message}`);
    }
  };

  const getSignatureStatusTag = (status) => {
    const config = {
      ongoing: { color: 'processing', icon: <ClockCircleOutlined />, label: 'En cours' },
      done: { color: 'success', icon: <CheckCircleOutlined />, label: 'Signé' },
      signed: { color: 'success', icon: <CheckCircleOutlined />, label: 'Signé' },
      declined: { color: 'error', icon: <CloseCircleOutlined />, label: 'Refusé' },
      expired: { color: 'warning', icon: <ClockCircleOutlined />, label: 'Expiré' },
      canceled: { color: 'default', icon: <CloseCircleOutlined />, label: 'Annulé' },
    };
    const c = config[status] || { color: 'default', icon: null, label: status };
    return <Tag color={c.color} icon={c.icon} style={{ fontSize: '10px', padding: '0 4px', lineHeight: '18px' }}>{c.label}</Tag>;
  };

  const getPaymentStatusTag = (status) => {
    const config = {
      waiting_signature: { color: 'default', icon: <ClockCircleOutlined />, label: '💳 Attente' },
      link_sent: { color: 'processing', icon: <CreditCardOutlined />, label: '💳 Envoyé' },
      paid: { color: 'success', icon: <CheckCircleOutlined />, label: '💳 Payé' },
      payment_error: { color: 'error', icon: <CloseCircleOutlined />, label: '💳 Erreur' },
    };
    const c = config[status] || { color: 'default', icon: null, label: status };
    return <Tag color={c.color} icon={c.icon} style={{ fontSize: '10px', padding: '0 4px', lineHeight: '18px' }}>{c.label}</Tag>;
  };

  const handleBackupTemplates = async () => {
    if (!isGoogleAuthenticated()) {
      message.error('Not authenticated with Google. Please connect Google Drive first.');
      window.location.href = getGoogleAuthUrl();
      return;
    }
    setBackingUpTemplates(true);
    try {
      const result = await backupAllTemplates();
      if (result.failed > 0) {
        message.warning(`Backup: ${result.success} OK, ${result.failed} erreurs`);
      } else {
        message.success(`${result.success} templates sauvegardés dans Supabase`);
      }
    } catch (error) {
      message.error('Erreur backup: ' + error.message);
    } finally {
      setBackingUpTemplates(false);
    }
  };

  const columns = [
    {
      title: 'Client',
      dataIndex: 'client_name',
      key: 'client_name',
      width: 150,
      render: (name) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          <div style={styles.clientAvatar}>
            {name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <Text strong style={{ color: colors.textPrimary, fontSize: typography.fontSize.sm }}>
            {name}
          </Text>
        </div>
      )
    },
    {
      title: 'Type',
      dataIndex: 'category',
      key: 'category',
      width: 90,
      render: (category) => {
        const categoryConfig = {
          'seabob': { icon: '🌊', label: 'Seabob' },
          'jetski': { icon: '🏍️', label: 'Jet-ski' },
          'bateaux': { icon: '🛥️', label: 'Bateaux' },
          'lunch': { icon: '🍽️', label: 'Lunch' }
        };
        const config = categoryConfig[category] || categoryConfig['bateaux'];
        return (
          <span style={{
            ...styles.tag,
            background: colors.bgTertiary,
            color: colors.textSecondary,
          }}>
            {config.icon} {config.label}
          </span>
        );
      }
    },
    {
      title: 'Date',
      dataIndex: 'charter_date',
      key: 'charter_date',
      width: 100,
      render: (date) => (
        <Text style={{ color: colors.textPrimary, fontSize: typography.fontSize.sm }}>
          {date ? dayjs(date, 'DD/MM/YYYY').format('DD/MM/YYYY') : '-'}
        </Text>
      )
    },
    {
      title: 'Documents',
      key: 'documents',
      width: 300,
      render: (_, record) => {
        const docIcons = {
          'Contrat': '📝',
          'Confirmation': '✅',
          'Facturation': '💰'
        };
        const docLabels = {
          'Contrat': 'Contrat',
          'Confirmation': 'Confirm.',
          'Facturation': 'Facture'
        };
        const isLegacy = record.source === 'legacy';

        if (isLegacy) {
          return (
            <div style={pill.container}>
              {['Contrat', 'Confirmation', 'Facturation'].map((type, i) => {
                const doc = record.documents[type];
                if (!doc || typeof doc !== 'string' || !doc.startsWith('http')) return null;
                return (
                  <React.Fragment key={type}>
                    {i > 0 && <div style={pill.divider} />}
                    <a href={doc} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'flex' }}>
                      <button style={pill.segment(true)}>
                        {docIcons[type]} {docLabels[type]}
                      </button>
                    </a>
                  </React.Fragment>
                );
              }).filter(Boolean)}
            </div>
          );
        }

        const canGenerate = record.documents['Contrat'] !== undefined;
        const segments = ['Contrat', 'Confirmation', 'Facturation'].map(type => {
          const doc = record.documents[type];
          if (doc && typeof doc === 'object') return { type, exists: true };
          if (canGenerate && (type === 'Confirmation' || type === 'Facturation')) return { type, exists: false };
          return null;
        }).filter(Boolean);

        if (segments.length === 0) return null;

        return (
          <div style={pill.container}>
            {segments.map(({ type, exists }, i) => (
              <React.Fragment key={type}>
                {i > 0 && <div style={pill.divider} />}
                {exists ? (
                  <button
                    style={pill.segment(true)}
                    onClick={() => handleDownloadDocument(record.documents[type])}
                    title={`Télécharger ${type}`}
                  >
                    <DownloadOutlined style={{ fontSize: 10 }} />
                    {docIcons[type]} {docLabels[type]}
                  </button>
                ) : (
                  <button
                    style={pill.segmentGhost}
                    onClick={() => type === 'Confirmation' ? handleGenerateConfirmation(record) : handleGenerateFacturation(record)}
                    title={`Générer ${type}`}
                  >
                    + {docIcons[type]} {docLabels[type]}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>
        );
      }
    },
    {
      title: 'Modifier',
      key: 'edit_actions',
      width: 150,
      render: (_, record) => {
        const docIcons = { 'Contrat': '📝', 'Confirmation': '✅', 'Facturation': '💰' };
        const docLabels = { 'Contrat': 'Contrat', 'Confirmation': 'Confirm.', 'Facturation': 'Facture' };
        const isLegacy = record.source === 'legacy';

        const editSegments = ['Contrat', 'Confirmation', 'Facturation'].map(type => {
          const doc = record.documents[type];
          const modifUrl = record.documents[`modif${type}`];
          if (isLegacy) {
            if (modifUrl && typeof modifUrl === 'string' && modifUrl.startsWith('http')) return { type, url: modifUrl };
            return null;
          }
          if (!doc || typeof doc !== 'object') return null;
          return { type, doc };
        }).filter(Boolean);

        if (editSegments.length === 0) return null;

        return (
          <div style={pill.container}>
            {editSegments.map(({ type, url, doc }, i) => (
              <React.Fragment key={`edit-${type}`}>
                {i > 0 && <div style={pill.divider} />}
                {url ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'flex' }}>
                    <button style={pill.segment(false)} title={`Modifier ${type}`}>
                      <EditOutlined style={{ fontSize: 10 }} />
                      {docIcons[type]} {docLabels[type]}
                    </button>
                  </a>
                ) : (
                  <button
                    style={pill.segment(false)}
                    onClick={() => handleEdit(doc)}
                    title={`Modifier ${type}`}
                  >
                    {doc.google_doc_id ? <GoogleOutlined style={{ fontSize: 10 }} /> : <EditOutlined style={{ fontSize: 10 }} />}
                    {docIcons[type]} {docLabels[type]}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>
        );
      }
    },
    // Yousign signature column — disabled for now
    // {
    //   title: 'Statut',
    //   key: 'signature',
    //   width: 130,
    //   render: (_, record) => { ... }
    // },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => {
        const isLegacy = record.source === 'legacy';
        const firstDoc = Object.values(record.documents).find(d => d && (typeof d === 'object' || (typeof d === 'string' && d.startsWith('http'))));
        if (!firstDoc) return null;

        return (
          <Space size="small">
            {/* Bouton Apercu - V2 only */}
            {!isLegacy && (
              <Button
                icon={<EyeOutlined />}
                onClick={() => handlePreview(firstDoc)}
                size="small"
                style={styles.editBtn}
                title="Aperçu"
              />
            )}

            {/* Bouton Supprimer - V2 only */}
            {!isLegacy && (
              <Popconfirm
                title="Supprimer ce charter?"
                description="Action irréversible"
                onConfirm={() => handleDeleteCharter(record.charter_id, record.documents)}
                okText="Oui"
                cancelText="Non"
              >
                <Button
                  icon={<DeleteOutlined />}
                  size="small"
                  style={{
                    ...styles.editBtn,
                    color: colors.accentError,
                    borderColor: colors.accentError,
                  }}
                  title="Supprimer"
                />
              </Popconfirm>
            )}
          </Space>
        );
      }
    }
  ];

  // Combined loading state
  const isLoading = loading || legacyLoading;

  return (
    <div style={styles.page}>
      <style>{`
        .mono-table .ant-table { background: #fff !important; }
        .mono-table .ant-table-thead > tr > th {
          background: #fafafa !important;
          border-bottom: 1px solid #f0f0f0 !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #999 !important;
          padding: 10px 16px !important;
        }
        .mono-table .ant-table-tbody > tr > td {
          background: #fff !important;
          border-bottom: 1px solid #f5f5f5 !important;
          padding: 12px 16px !important;
        }
        .mono-table .ant-table-tbody > tr:hover > td {
          background: #fafafa !important;
        }
        .mono-table .ant-table-container { border-radius: 0 !important; }
        .mono-table .ant-pagination { padding: 14px 24px !important; }
      `}</style>
      {/* Header */}
      <div style={styles.header}>
        {/* Left: title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={styles.headerIcon}>📄</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
              Liste des Documents
            </div>
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 2, fontWeight: 500 }}>
              {documents.length + legacyDocuments.length} documents · V2 ({documents.length}) V1 ({legacyDocuments.length})
            </div>
          </div>
        </div>

        {/* Center: search */}
        <div style={{ flex: 1, maxWidth: 320, margin: '0 24px' }}>
          <Input
            placeholder="Rechercher par client…"
            prefix={<SearchOutlined style={{ color: '#999', fontSize: 13 }} />}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            style={{
              borderRadius: 8,
              background: '#fff',
              border: '1.5px solid #d9d9d9',
              height: 36,
              fontSize: 13,
              boxShadow: 'none',
            }}
          />
        </div>

        {/* Right: actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Factures today — compact badge */}
          <div
            onClick={() => setInvoiceCounterModalVisible(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 8,
              cursor: 'pointer',
              background: todayInvoices.length > 0 ? '#111' : '#f5f5f5',
              border: `1px solid ${todayInvoices.length > 0 ? '#111' : '#e5e5e5'}`,
              transition: 'all 0.15s',
            }}
          >
            <FileTextOutlined style={{ fontSize: 13, color: todayInvoices.length > 0 ? '#fff' : '#aaa' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: todayInvoices.length > 0 ? '#fff' : '#888' }}>
              {todayInvoices.length} facture{todayInvoices.length !== 1 ? 's' : ''} aujourd'hui
            </span>
          </div>

          {!googleStatus.authenticated && (
            <Button
              size="small"
              onClick={handleConnectGoogle}
              icon={<GoogleOutlined />}
              style={{ ...styles.secondaryBtn, height: 36 }}
            >
              Connect Google
            </Button>
          )}

          <Button
            icon={<SaveOutlined />}
            onClick={handleBackupTemplates}
            loading={backingUpTemplates}
            style={{ ...styles.secondaryBtn, height: 36 }}
            title="Backup templates"
          >
            Backup
          </Button>

          <Button
            icon={<ReloadOutlined />}
            onClick={() => { fetchDocuments(); fetchAllLegacyData(); }}
            loading={isLoading}
            style={{ ...styles.secondaryBtn, height: 36 }}
          >
            Actualiser
          </Button>
        </div>
      </div>

      {/* Active Filters Display */}
      {(categoryFilter !== 'all' || sourceFilter !== 'all') && (
        <div style={{ marginBottom: spacing.lg, display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
          <Text style={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>Filtres actifs:</Text>
          {sourceFilter !== 'all' && (
            <Tag
              closable
              onClose={() => handleSourceFilter('all')}
              style={{
                ...styles.tag,
                background: colors.bgDark,
                color: colors.textInverse,
                border: 'none',
              }}
            >
              {sourceFilter === 'v2' ? '🆕 V2' : '📜 V1'}
            </Tag>
          )}
          {categoryFilter !== 'all' && (
            <Tag
              closable
              onClose={() => handleCategoryFilter('all')}
              style={{
                ...styles.tag,
                background: colors.bgTertiary,
                color: colors.textPrimary,
                border: `1px solid ${colors.borderMedium}`,
              }}
            >
              {categoryFilter === 'bateaux' ? '🛥️ Bateaux' :
               categoryFilter === 'seabob' ? '🌊 Seabob' :
               categoryFilter === 'jetski' ? '🏍️ Jet-ski' : '🍽️ Lunch'}
            </Tag>
          )}
          <Button
            type="link"
            size="small"
            onClick={() => {
              handleSourceFilter('all');
              handleCategoryFilter('all');
            }}
            style={{ color: colors.textMuted }}
          >
            Effacer tous les filtres
          </Button>
        </div>
      )}

      {/* Documents Table */}
      <div style={styles.tableCard}>
        <div style={styles.tableCardHeader}>
          <span style={{ fontSize: '20px' }}>📋</span>
          <span style={{ color: colors.textPrimary, fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.lg }}>
            Documents
          </span>
          <span style={{
            ...styles.tag,
            background: colors.bgDark,
            color: colors.textInverse,
          }}>{filteredDocuments.length}</span>
        </div>
        <Table
          className="mono-table"
          columns={columns}
          dataSource={filteredDocuments}
          rowKey="charter_id"
          loading={isLoading}
          pagination={{
            pageSize: 15,
            showTotal: (total) => `Total: ${total} document${total > 1 ? 's' : ''}`,
            size: 'default',
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '25', '50'],
          }}
          size="small"
          scroll={{ x: 'max-content' }}
          style={{ padding: spacing.lg }}
        />
      </div>

      {/* Modal Aperçu */}
      <Modal
        title={`Aperçu - ${selectedDocument?.type}`}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            Fermer
          </Button>,
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setPreviewVisible(false);
              handleEdit(selectedDocument);
            }}
          >
            Modifier
          </Button>
        ]}
      >
        {selectedDocument && (
          <div>
            <p><strong>Client:</strong> {selectedDocument.client_name}</p>
            <p><strong>Date de création:</strong> {dayjs(selectedDocument.created_at).format('DD/MM/YYYY HH:mm')}</p>
            <Divider>Données du document</Divider>
            <Row gutter={[16, 16]}>
              {Object.entries(selectedDocument.document_data || {}).map(([key, value]) => (
                <Col span={12} key={key}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ textTransform: 'capitalize' }}>
                      {key.replace(/_/g, ' ')}:
                    </strong>
                    <div style={{ marginTop: '4px', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                      {value || '-'}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Modal>

      {/* Modal Modification */}
      <Modal
        title={`Modifier - ${selectedDocument?.type}`}
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        width={900}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleRegenerateDocument}
        >
          <Row gutter={16}>
            {selectedDocument && Object.entries(selectedDocument.document_data || {}).map(([key, value]) => (
              <Col span={12} key={key}>
                <Form.Item
                  name={key}
                  label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                >
                  <Input />
                </Form.Item>
              </Col>
            ))}
          </Row>

          <Divider />

          <Space>
            <Button onClick={() => setEditVisible(false)}>
              Annuler
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={regenerating}
              icon={<ReloadOutlined />}
            >
              Régénérer le document
            </Button>
          </Space>
        </Form>
      </Modal>

      {/* Modal Génération Confirmation */}
      <Modal
        title="Générer la Confirmation"
        open={confirmationModalVisible}
        onCancel={() => setConfirmationModalVisible(false)}
        onOk={confirmGenerateConfirmation}
        okText="Générer"
        cancelText="Annuler"
        confirmLoading={regenerating}
      >
        <p>Voulez-vous générer la Confirmation pour ce charter ?</p>
        <p><strong>Client:</strong> {selectedCharter?.client_name}</p>
        <p><strong>Date charter:</strong> {selectedCharter?.charter_date}</p>
        <Divider />
        <p><strong>Date du jour:</strong> {dayjs().format('DD/MM/YYYY')}</p>
        <p className="text-gray-500">La date du jour sera utilisée comme "today_date" dans le document.</p>
      </Modal>

      {/* Modal Génération Facturation */}
      <Modal
        title="Générer la Facturation"
        open={facturationModalVisible}
        onCancel={() => {
          setFacturationModalVisible(false);
          invoiceForm.resetFields();
        }}
        footer={null}
      >
        <p><strong>Client:</strong> {selectedCharter?.client_name}</p>
        <p><strong>Date charter:</strong> {selectedCharter?.charter_date}</p>
        <Divider />
        <Form
          form={invoiceForm}
          layout="vertical"
          onFinish={confirmGenerateFacturation}
        >
          <Form.Item
            name="invoice_number"
            label="Numéro de Facture"
            rules={[{ required: true, message: 'Le numéro de facture est requis' }]}
            tooltip="Format: DDMMYYYY-X (ex: 10012025-1)"
          >
            <Input placeholder="10012025-1" />
          </Form.Item>

          {(selectedCharter?.category === 'seabob' || selectedCharter?.category === 'lunch') && (
            <Form.Item
              name="charterer"
              label="Charterer (Nom du client facturé)"
              rules={[{ required: false, message: 'Le charterer est optionnel' }]}
            >
              <Input placeholder="Nom du charterer (optionnel)" />
            </Form.Item>
          )}

          <p className="text-gray-500 mb-4">
            Date du jour: <strong>{dayjs().format('DD/MM/YYYY')}</strong><br />
            Cette date sera utilisée comme "today_date" dans le document.
          </p>
          <Space>
            <Button onClick={() => {
              setFacturationModalVisible(false);
              invoiceForm.resetFields();
            }}>
              Annuler
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={regenerating}
            >
              Générer la Facturation
            </Button>
          </Space>
        </Form>
      </Modal>

      {/* Modal Yousign Send — disabled for now */}

      {/* Modal Today's Invoices */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
            <FileTextOutlined style={{ color: '#52c41a' }} />
            <span>Factures du jour - {dayjs().format('DD/MM/YYYY')}</span>
            <Tag color="green" style={{ marginLeft: spacing.sm }}>{todayInvoices.length}</Tag>
          </div>
        }
        open={invoiceCounterModalVisible}
        onCancel={() => setInvoiceCounterModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setInvoiceCounterModalVisible(false)}>
            Fermer
          </Button>,
          <Button key="refresh" icon={<ReloadOutlined />} onClick={fetchTodayInvoices}>
            Actualiser
          </Button>
        ]}
        width={700}
      >
        {todayInvoices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: spacing.xxl, color: colors.textMuted }}>
            <FileTextOutlined style={{ fontSize: '48px', marginBottom: spacing.md, opacity: 0.3 }} />
            <p>Aucune facture générée aujourd'hui</p>
          </div>
        ) : (
          <Table
            dataSource={todayInvoices}
            rowKey="id"
            pagination={false}
            size="small"
            columns={[
              {
                title: '#',
                key: 'index',
                width: 50,
                render: (_, __, index) => (
                  <Tag color="blue">{index + 1}</Tag>
                ),
              },
              {
                title: 'N° Facture',
                dataIndex: 'document_data',
                key: 'invoice_number',
                render: (data) => (
                  <Text strong style={{ color: '#52c41a' }}>
                    {data?.invoice_number || data?.numero_facture || '-'}
                  </Text>
                ),
              },
              {
                title: 'Client',
                dataIndex: 'client_name',
                key: 'client_name',
                render: (name) => <Text>{name}</Text>,
              },
              {
                title: 'Catégorie',
                dataIndex: 'category',
                key: 'category',
                render: (cat) => {
                  const categoryLabels = {
                    bateaux: '🚤 Yacht',
                    seabob: '🌊 Seabob',
                    jetski: '🏍️ Jet-ski',
                    lunch: '🍽️ Lunch',
                    custom: '✏️ Custom',
                  };
                  return <Tag>{categoryLabels[cat] || cat}</Tag>;
                },
              },
              {
                title: 'Heure',
                dataIndex: 'created_at',
                key: 'created_at',
                render: (date) => (
                  <Text type="secondary">{dayjs(date).format('HH:mm')}</Text>
                ),
              },
              {
                title: 'Lien',
                dataIndex: 'google_doc_url',
                key: 'link',
                render: (url) => url ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<LinkOutlined />}
                    onClick={() => window.open(url, '_blank')}
                  >
                    Ouvrir
                  </Button>
                ) : '-',
              },
            ]}
          />
        )}
      </Modal>

      {/* Charter Edit Drawer */}
      <CharterEditDrawer
        open={charterEditVisible}
        onClose={() => { setCharterEditVisible(false); setCharterEditTarget(null); }}
        charter={charterEditTarget}
        onSuccess={() => { fetchDocuments(); fetchTodayInvoices(); }}
      />
    </div>
  );
}

export default DocumentListPage;
